import { ActionsDrop } from "components/actions-drop"
import { Button } from "components/Button/Button"
import { DataTable, TableCol } from "components/DataTable/DataTable"
import { DialogModal } from "components/dialog-modal/dialog-modal"
import { useLangContext } from "context/Lang"
import { MapPlusIcon, Plus, ZapIcon, ZapOffIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { routes } from "routes"
import { AsyncProcess, } from "services/AsyncProcess"
import { setToast } from "storeRedux/slices/toast"
import { ReplaceWords } from "utils/ReplaceWords"

import { Loader } from "components/Loader/Loader"
import { Message } from "primereact/message"
import { RootStateType } from "storeRedux/store"
import { MapForm } from "forms/map-form"
import { UserTypes } from "assets/Roles"
import { StatusFlags, StatusMap, StatusType } from "utils/Status"
import { StatusLabel } from "components/status-label"


export const Maps = () => {
    const { gW } = useLangContext();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // States
    const [loading, setLoading] = useState<boolean>(true);
    const [maps, setMaps] = useState<MapType[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });

    const [openMapModal, setOpenMapModal] = useState<boolean>(false);
    const [selectedMap, setSelectedMap] = useState<MapType | null>(null); // لحفظ الخريطة المختارة عند التعديل
    const [permission, setPermission] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const { user } = useSelector((state: RootStateType) => state.auth);


    // --- جلب الخرائط ---
    const fetchMaps = async () => {
        setLoading(true);
        await AsyncProcess({
            type: "get",
            url: routes.api.maps,
            setError: (err) => {
                setLoading(false);
                dispatch(setToast({ detail: gW(err?.error || "happen-error"), severity: "error" }));
            },
            effectProcess: [(res) => {
                setLoading(false);
                if (res?.success === false) {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                    setPermission(false);
                } else {
                    setMaps(res.data || []);
                    setTotalRecords(res?.data?.length || 0);
                    setPermission(true);
                }
            }],
        });
    };

    const handleSaveMap = (mapData: MapType) => {
        setSaving(true);
        AsyncProcess({
            type: "post",
            url: routes.api.maps,
            data: mapData,
            setError: () => setSaving(false),
            effectProcess: [(res) => {
                setSaving(false);
                if (res?.success === false) {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                } else {
                    dispatch(setToast({
                        detail: gW(mapData.id ? "update-success" : "create-success"),
                        severity: "success"
                    }));
                    setOpenMapModal(false);
                    setSelectedMap(null);
                    fetchMaps(); // تحديث القائمة بعد الحفظ
                }
            }]
        });
    };

    const handleToggleMapStatus = (map: MapType) => {
        // تحديد الحالة الجديدة بناءً على الحالة الحالية
        // const newStatus = map.status === "ACTIVE" ? "PASSIVE" : "ACTIVE";
        
        setLoading(true);
        AsyncProcess({
            type: "post", // أو post حسب تصميم الـ API لديك
            url: `${routes.api.maps}/${map.id}/toggle-status`, // رابط الـ API الخاص بتعديل الحالة
            // data: { status: newStatus },
            setError: (err) => {
                setLoading(false);
                dispatch(setToast({ detail: gW(err?.error || "happen-error"), severity: "error" }));
            },
            effectProcess: [(res) => {



                if (res?.success === false) {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                    setLoading(false);
                } else {
                    setLoading(false);

                    const item:MapType = res?.data;  
                    dispatch(setToast({
                        detail: gW(item?.status === StatusFlags.ACTIVE ? "map-activated-success" : "map-deactivated-success"),
                        severity: "success"
                    }));

                    setMaps(m=>[...(m||[])?.map(m=>m?.id===item?.id ? item:m)]);
                    
                   
                    // fetchMaps(); // إعادة جلب البيانات لتحديث الجدول بالحالة الجديدة
                }
            }]
        });
    };


    useEffect(() => {
        fetchMaps();
    }, []);

    const onPageChange = (event: any) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };



    const columns: TableCol<MapType>[] = [
        { label: gW("map-id"), name: "id" },
        { label: gW("map-name"), name: "title" },
        { label: gW("description"), name: "description" },
        { 
            label: gW("status"), 
            name: "status",
            // إضافة تصميم مرئي للحالة (Badge) داخل الجدول لتبدو احترافية
            cell: (map: MapType) => {
                return <StatusLabel status={map?.status} />
            }
        },
        {
            label: gW("actions"),
            cell: (map: MapType) => (
                <ActionsDrop
                    onEdit={() => {
                        setSelectedMap(map); // وضع بيانات الخريطة في الـ State
                        setOpenMapModal(true); // فتح المودال للتعديل
                    }}
                    onGo={() => {
                        navigate(ReplaceWords(routes.dashboardRroutePage, {
                            route: routes.dashboardRroutes.maps,
                            id: map?.id
                        }));
                    }}
                    morActions={[
                        {
                            label: map.status === StatusFlags.ACTIVE ? gW("passive") : gW("active"),
                            onClick: () => handleToggleMapStatus(map),
                            icon: map.status === StatusFlags.ACTIVE ? <ZapOffIcon size={16} className="text-red-500" /> : <ZapIcon size={16} className="text-green-500" />
                        }
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <DialogModal
                size="md"
                isOpen={openMapModal}
                onClose={() => {
                    setOpenMapModal(false);
                    setSelectedMap(null); // تنظيف الاختيار عند الإغلاق
                }}
                title={selectedMap ? gW("update-map") : gW("add-map")}
            >
                {openMapModal && (
                    <MapForm
                        map={selectedMap || undefined}
                        onSubmit={(data: MapType) => handleSaveMap(data)}
                        loading={saving}
                    />
                )}
                {saving && <Loader />}
            </DialogModal>

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">{gW("maps")}</h1>
                <div className="flex gap-3">
                    {user?.userType===UserTypes.MANAGER && (
                        <Button variant="outline" onClick={() => {
                            setSelectedMap(null); // التأكد أن المودال سيفتح كـ "إضافة"
                            setOpenMapModal(true);
                        }}>
                            <MapPlusIcon className="mr-2" /> {gW("add-map")}
                        </Button>
                    )}
                </div>
            </div>

            {loading && (
                <div className="w-full p-16 flex justify-center items-center">
                    <Loader />
                </div>
            )}

            {!permission && !loading && (
                <Message className="w-full" severity="warn" text={gW("no-permission")} />
            )}

            {permission && !loading && (
                <DataTable<MapType>
                    data={maps}
                    columns={columns}
                    rowKey={(mapItem) => (mapItem?.id || "").toString()}
                    loading={loading}
                    totalRecords={totalRecords}
                    rows={lazyParams.rows}
                    first={lazyParams.first}
                    onPageChange={onPageChange}
                />
            )}
        </>
    );
};